import { FC, ReactNode, useMemo } from "react";
import { WebClipV1SessionPart, SessionEventKind, SessionPartKind } from "../session.types";
import styled from "@emotion/styled";
import { MDSSessionPartContainer } from "@/mds/components/sessions/session-parts/session-part.style";
import { SessionPartProps } from "@/mds/components/sessions/session-parts/session-part.types";
import { MDSBubble } from "@/mds/components/bubble/bubble";
import { MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble/bubble.types";
import { WEB_CLIP_MAX_HEIGHT_SIZE_PX } from "@/mds/components/sessions/session.constants";
import { isNumber } from "lodash-es";
import { motion, AnimatePresence } from "motion/react";
import { MDSSessionSentBadge } from "../session-sent-badge";
import { SessionSentBadgeColor } from "../session-sent-badge/session-sent-badge.types";
import {
  useIsLastPartInSessionGroup,
  useIsLastUserMessagePartInSessionHistory,
} from "@/mds/components/sessions/session-context/mds-session-context.hooks";
import { SessionClippedTextContent } from "@/mds/components/sessions/session-parts/common/session-clipped-text-content";

type Props = SessionPartProps<WebClipV1SessionPart>;

export const MDSSessionWebClipV1Part: FC<Props> = ({ part, context }) => {
  const isLastInGroup = useIsLastPartInSessionGroup({ part });
  const isLastUserMessage = useIsLastUserMessagePartInSessionHistory({ part });

  const WrapperComponent: FC<IWrapperComponentProps> = (() => {
    if (context.eventKind === SessionEventKind.AgentActionV1) {
      return AgentActionWrapper;
    }

    if (part.kind === SessionPartKind.WebClipV1) {
      return WebClipWrapper;
    }

    return StandardWrapper;
  })();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const meta: Record<string, unknown> | undefined = part._meta;

  const completionPercentage = useMemo(() => {
    const percentage = meta?.completion_percentage as number | undefined;

    if (isNumber(percentage)) {
      return percentage;
    }

    return 1.0;
  }, [meta?.completion_percentage]);

  const isPendingCompletion = useMemo(() => {
    return completionPercentage < 1.0;
  }, [completionPercentage]);

  const isSentBadgeRenderable = useMemo(() => {
    return isLastInGroup && isLastUserMessage;
  }, [isLastInGroup, isLastUserMessage]);

  const isSentBadgeVisible = useMemo(() => {
    if (!isSentBadgeRenderable) {
      return false;
    }

    return !isPendingCompletion;
  }, [isSentBadgeRenderable, isPendingCompletion]);

  return (
    <>
      <WrapperComponent isLastInGroup={isLastInGroup}>
        <Container eventKind={context.eventKind}>
          <SessionClippedTextContent
            clippedTextContent={part.clipped_text_content}
            clippedRichTextContent={part.clipped_rich_text_content}
            isLastInGroup={isLastInGroup}
          />
          {!part.clipped_text_content && part.clipped_image_uploaded_file_url && (
            <WebClipImage
              src={part.clipped_image_uploaded_file_url}
              alt={part.captured_website_title ?? "Clipped Web Page"}
              hasClippedTextContent={!!part.clipped_text_content}
            />
          )}
          <AnimatePresence>
            {isPendingCompletion && (
              <LoadingOverlay exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                <LoadingContent>
                  <SmallSpinner
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      ease: "linear",
                      repeat: Infinity,
                    }}
                  />
                </LoadingContent>
              </LoadingOverlay>
            )}
          </AnimatePresence>
        </Container>
      </WrapperComponent>
      {isSentBadgeRenderable && (
        <MDSSessionSentBadge
          color={SessionSentBadgeColor.Green}
          isVisible={isSentBadgeVisible}
          label="Sent to Mem"
        />
      )}
    </>
  );
};

interface IWrapperComponentProps {
  children: ReactNode;
  isLastInGroup?: boolean;
}

const AgentActionWrapper: FC<IWrapperComponentProps> = ({ children }) => {
  return <>{children}</>;
};

const WebClipWrapper: FC<IWrapperComponentProps> = ({ children, isLastInGroup }) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Image}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {children}
    </Bubble>
  );
};

const StandardWrapper: FC<IWrapperComponentProps> = ({ children, isLastInGroup }) => {
  return (
    <Bubble
      variant={MDSBubbleVariant.Grey}
      align={MDSBubbleAlignment.Right}
      isLastInGroup={isLastInGroup}
    >
      {children}
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)<{ isLastInGroup?: boolean }>`
  width: 100%;
  margin-bottom: ${({ theme, isLastInGroup }) =>
    isLastInGroup ? theme.spacing.xxs : theme.spacing.sm};
`;

const Container = styled(MDSSessionPartContainer)<{
  eventKind: SessionEventKind;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-height: ${WEB_CLIP_MAX_HEIGHT_SIZE_PX}px;
  align-items: center;
  margin-bottom: ${({ theme, eventKind }) =>
    eventKind === SessionEventKind.AgentActionV1 ? theme.spacing.xs : "0"};
  position: relative;
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const WebClipImage = styled.img<{ hasClippedTextContent: boolean }>`
  object-fit: cover;
  object-position: top;
  border-radius: 16px 16px 0 16px;
  border-bottom: none;
  width: 350px;
  height: 220px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const SmallSpinner = styled(motion.div)`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-radius: 50%;
  border-top-color: ${({ theme }) => theme.colors.grey.x600};
  mask-image: linear-gradient(
    to left,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0.6) 65%,
    rgba(0, 0, 0, 0) 100%
  );
  will-change: transform;
`;
